import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    type ComplianceRequirementListView,
    useOrgAuthStore
} from "@solomon-apps/shared";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import {
    isSessionExpiredError,
    useClientOnboardingApplications
} from "@/hooks/useClientOnboardingApplications";
import { useClientPlatformAccess } from "@/hooks/useClientPlatformAccess";
import { fetchClientComplianceRequirements } from "@/services/clientCompliance";
import type { ApplicationBundle } from "@/services/clientOnboarding";
import { isClientConsoleAvailable } from "@/utils/clientConsoleAccess";

export { selectedContextHasPlatformEligibility } from "@/hooks/useClientPlatformAccess";

/**
 * Post-auth gate that mirrors
 * `claude-docs/intents/yaas-client/onboarding.md` "Gate And Entry":
 *   - The applicant lands on `/onboarding` while their application is
 *     in `draft` / `in_review` / `awaiting_agreements` / `rejected`.
 *   - The main Client Console (Dashboard / Positions / Reporting /
 *     Policies / Settings / Admin) becomes available only after the
 *     application is `approved`, or the selected relationship is an active,
 *     eligible issuer self-client (which has no onboarding application).
 *
 * `awaiting_agreements` is deliberately on the wrong side of that line: the
 * issuer has approved, but the agreements are still out for signature and
 * the last signature is what makes the application `approved`. Nothing here
 * special-cases it — it falls through to the same redirect `in_review` does.
 *
 * The gate sits inside `AuthGuard` — by the time we run, we know the
 * caller is authenticated; the only question is whether they're past
 * onboarding. We look up the caller's application via
 * `listClientApplications` (the BD-provisioning flow pre-creates one
 * Draft row per applicant) and redirect away from any non-onboarding
 * route until the status flips to Approved.
 *
 * Missing rows for external clients and API errors are routed to `/onboarding` so
 * manually-provisioned, drifted, or temporarily-unverifiable users do
 * not silently bypass the onboarding surface. The one exception is a
 * session-expired 401 (see `isSessionExpiredError`): it can never heal,
 * so the gate clears auth and routes to `/login` instead of letting the
 * query retry/refetch cycle hammer the API.
 *
 * `FEATURE_FLAGS.clientConsoleAccess` holds external clients on onboarding
 * even after approval. Active, eligible issuer self-clients can enter the
 * console while that external-client release flag is off.
 */
const ONBOARDING_PATH = "/onboarding";
const COMPLIANCE_PATH = "/compliance";
const API_KEYS_PATH = "/api-keys";

type OnboardingGateStatus = ApplicationBundle["application"]["status"] | null;
type OnboardingGateDecision =
    | "allow"
    | "redirect-to-console"
    | "redirect-to-onboarding"
    | "redirect-to-compliance";

export function resolveOnboardingGateDecision({
    approvalAcknowledgedAt,
    complianceBlocksClientWrites = false,
    complianceRedirectEnabled = false,
    consoleAccessEnabled = true,
    isSelfClient = false,
    pathname,
    platformEligible = false,
    status
}: {
    /**
     * The application's `approvalAcknowledgedAt` — the timestamp set the first
     * time the client opens the post-approval welcome celebration. Only
     * consulted when `status` is `approved`. The core rule lives here (not in
     * the component) so it stays unit-tested:
     *   - an explicit `null` means "approved but not yet welcomed" → route the
     *     first sign-in through /onboarding once so the celebration can show;
     *   - a set timestamp means already welcomed → allow;
     *   - `undefined`/omitted (older API that predates the column, or callers
     *     that don't pass it) is treated as already-welcomed → allow, so the
     *     gate keeps its prior "approved → allow" behaviour if the backend
     *     hasn't shipped yet.
     */
    approvalAcknowledgedAt?: string | null;
    complianceBlocksClientWrites?: boolean;
    complianceRedirectEnabled?: boolean;
    /**
     * `FEATURE_FLAGS.clientConsoleAccess` — whether external clients can enter
     * the console. When false, external clients route back to onboarding
     * regardless of application status, so an approved applicant waits on the
     * onboarding surface instead of landing in the app. Defaults to `true` so
     * callers that predate the flag (and the existing unit tests) keep the
     * original status-driven behaviour.
     */
    consoleAccessEnabled?: boolean;
    /** Taken from authenticated context discovery for the selected relationship. */
    isSelfClient?: boolean;
    /** Server-resolved eligibility for the selected issuer relationship.
     * Missing or unknown is deliberately unavailable. */
    platformEligible?: boolean;
    pathname: string;
    status: OnboardingGateStatus;
}): OnboardingGateDecision {
    // The release flag cannot turn an approved minting-only relationship into a
    // platform relationship. The onboarding surface remains available so the
    // applicant can complete and inspect its agreement lifecycle.
    if (!platformEligible) {
        return isEligibilityCompletionPath(pathname)
            ? "allow"
            : "redirect-to-onboarding";
    }
    // Console hidden for external clients. This runs
    // before the status branches on purpose — approval must not open the app
    // while the flag is off. The compliance redirect is skipped for the same
    // reason: /compliance lives inside the console we are hiding.
    if (
        !isClientConsoleAvailable({
            consoleAccessEnabled,
            isSelfClient,
            platformEligible
        })
    ) {
        return isOnboardingPath(pathname) ? "allow" : "redirect-to-onboarding";
    }
    // The issuer self-client flow deliberately creates no application or
    // invitation. Eligibility and activation above remain mandatory.
    if (isSelfClient) {
        if (
            complianceRedirectEnabled &&
            complianceBlocksClientWrites &&
            !isCompliancePath(pathname)
        ) {
            return "redirect-to-compliance";
        }
        return isOnboardingPath(pathname) ? "redirect-to-console" : "allow";
    }
    if (status === "approved") {
        // Only an explicit null withholds the "allow" — see the prop doc.
        const acknowledged = approvalAcknowledgedAt !== null;
        // Approved + already welcomed → into the console. The one exception is
        // the first sign-in after approval (acknowledged === false): route it
        // through /onboarding once so the celebration shows, then it acks and
        // every later sign-in lands straight in the console. Already being on
        // /onboarding allows through so the celebration can actually render;
        // approval acknowledgement remains required for external clients.
        if (!acknowledged) {
            if (isOnboardingPath(pathname)) {
                return "allow";
            }
            return "redirect-to-onboarding";
        }
        if (
            complianceRedirectEnabled &&
            complianceBlocksClientWrites &&
            !isCompliancePath(pathname)
        ) {
            return "redirect-to-compliance";
        }
        if (acknowledged || isOnboardingPath(pathname)) {
            return "allow";
        }
    }
    if (isOnboardingPath(pathname)) {
        return "allow";
    }
    return "redirect-to-onboarding";
}

/** The current context header is the authority for a client request. Never
 * infer access from the application or from another relationship's record. */
export function shouldEvaluateComplianceGate({
    approvalAcknowledgedAt,
    isSelfClient = false,
    status
}: {
    approvalAcknowledgedAt?: string | null;
    isSelfClient?: boolean;
    status: OnboardingGateStatus;
}): boolean {
    if (isSelfClient) return true;
    if (status === "approved") {
        return approvalAcknowledgedAt !== null;
    }
    return false;
}

export function OnboardingGate({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    const { pathname } = useLocation();
    const tokens = useOrgAuthStore((s) => s.tokens);
    const selectedClientContextUuid = useOrgAuthStore(
        (s) => s.selectedClientContextUuid
    );
    const {
        consoleAvailable,
        isPending: platformAccessPending,
        isSettled: platformAccessSettled,
        isSelfClient,
        platformEligible
    } = useClientPlatformAccess({ refreshOnMount: true });
    // Resolve the relationship before fetching onboarding. Self-clients have
    // no application; its availability must not become an entry requirement.
    const { data, error, isLoading, isError } = useClientOnboardingApplications(
        {
            enabled: platformAccessSettled && !isSelfClient
        }
    );
    const bundle = data?.data?.[0];
    const status = bundle?.application.status ?? null;
    const approvalAcknowledgedAt = bundle?.application.approvalAcknowledgedAt;
    const authReady = Boolean(
        tokens && tokens.expiresAt >= Date.now() + 30_000
    );
    const shouldCheckCompliance =
        FEATURE_FLAGS.clientComplianceSurface &&
        !isClientSecurityOnlyPath(pathname) &&
        // Self-clients retain console access during the external-client hold.
        consoleAvailable &&
        shouldEvaluateComplianceGate({
            approvalAcknowledgedAt,
            isSelfClient,
            status
        });
    const {
        data: complianceRequirements,
        isLoading: complianceLoading,
        isError: complianceIsError
    } = useQuery<ComplianceRequirementListView, Error>({
        queryKey: [
            "yaas-client",
            "compliance-requirements",
            selectedClientContextUuid
        ],
        queryFn: fetchClientComplianceRequirements,
        enabled:
            authReady &&
            Boolean(selectedClientContextUuid) &&
            shouldCheckCompliance,
        staleTime: 30_000
    });

    if (isLoading && !isSelfClient) {
        return <GateLoading />;
    }

    // Context discovery is authorization input, rather than optional selector
    // data. Do not render a platform route from a previous relationship while
    // a fresh response is pending or unavailable.
    // Onboarding is also the fallback for unavailable contexts. Keep its
    // existing page mounted while acknowledgement reconciles discovery;
    // replacing it with loading restarts mount effects and the success card.
    if (platformAccessPending && !(isOnboardingPath(pathname) && data)) {
        return <GateLoading />;
    }
    if (
        !isEligibilityCompletionPath(pathname) &&
        (!platformAccessSettled || !platformEligible)
    ) {
        return <Navigate to={ONBOARDING_PATH} replace />;
    }

    if (isError && !isSelfClient) {
        // A 401 that survived orgAuthFetch's refresh round-trip means the
        // session cannot recover (e.g. the user row is gone server-side while
        // the JWT stays signature-valid). Retrying or refetching on the
        // normal cadence would hammer the API with guaranteed-401 calls, so
        // stop here: clear the dead session and route to login.
        if (isSessionExpiredError(error)) {
            return <SessionExpiredRedirect />;
        }
        if (typeof console !== "undefined") {
            console.warn(
                "OnboardingGate: failed to load applications; routing to onboarding"
            );
        }
        if (!isOnboardingPath(pathname)) {
            return <Navigate to={ONBOARDING_PATH} replace />;
        }
        return <>{children}</>;
    }

    const baseDecision = resolveOnboardingGateDecision({
        approvalAcknowledgedAt,
        consoleAccessEnabled: FEATURE_FLAGS.clientConsoleAccess,
        isSelfClient,
        pathname,
        platformEligible,
        status
    });
    if (baseDecision === "redirect-to-onboarding") {
        return <Navigate to={ONBOARDING_PATH} replace />;
    }
    if (
        shouldCheckCompliance &&
        complianceLoading &&
        !isCompliancePath(pathname)
    ) {
        return <GateLoading />;
    }
    if (complianceIsError && typeof console !== "undefined") {
        console.warn(
            "OnboardingGate: failed to load compliance requirements; allowing console and leaving write gates to the API"
        );
    }

    // The acknowledgement rule (null → first-login celebration, set/absent →
    // allow) lives inside resolveOnboardingGateDecision so it stays unit-tested.
    const decision = resolveOnboardingGateDecision({
        approvalAcknowledgedAt,
        complianceBlocksClientWrites: FEATURE_FLAGS.clientComplianceSurface
            ? (complianceRequirements?.summary.blocksClientWrites ?? false)
            : false,
        complianceRedirectEnabled: FEATURE_FLAGS.clientComplianceSurface,
        consoleAccessEnabled: FEATURE_FLAGS.clientConsoleAccess,
        isSelfClient,
        pathname,
        platformEligible,
        status
    });

    if (decision === "redirect-to-onboarding") {
        return <Navigate to={ONBOARDING_PATH} replace />;
    }
    if (decision === "redirect-to-compliance") {
        return <Navigate to={COMPLIANCE_PATH} replace />;
    }
    if (decision === "redirect-to-console") {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
}

function isOnboardingPath(pathname: string): boolean {
    return (
        pathname === ONBOARDING_PATH ||
        pathname.startsWith(`${ONBOARDING_PATH}/`)
    );
}

function isCompliancePath(pathname: string): boolean {
    return (
        pathname === COMPLIANCE_PATH ||
        pathname.startsWith(`${COMPLIANCE_PATH}/`)
    );
}

/** Required evidence is part of becoming eligible, so it stays available
 * while all platform business routes remain closed. */
function isEligibilityCompletionPath(pathname: string): boolean {
    return isOnboardingPath(pathname) || isCompliancePath(pathname);
}

export function isClientSecurityOnlyPath(pathname: string): boolean {
    return (
        pathname === API_KEYS_PATH || pathname.startsWith(`${API_KEYS_PATH}/`)
    );
}

/** Standard session-expired handling: drop the dead tokens, then land on
 *  /login. `clearAuth` runs in an effect (not during render) so the store
 *  write never happens mid-render; the redirect itself doesn't depend on
 *  it because /login sits outside `AuthGuard`. */
function SessionExpiredRedirect(): React.ReactElement {
    const clearAuth = useOrgAuthStore((s) => s.clearAuth);
    React.useEffect(() => {
        clearAuth();
    }, [clearAuth]);
    return <Navigate to="/login" replace />;
}

function GateLoading(): React.ReactElement {
    return (
        <div className="flex items-center justify-center h-screen w-screen bg-surface-ground">
            <Text variant="small" className="text-foreground-muted">
                Loading…
            </Text>
        </div>
    );
}
